interface TimestampedMessage {
  created_at?: string;
}

const toTimestamp = (message: TimestampedMessage): number => {
  if (!message.created_at) return Number.POSITIVE_INFINITY;
  const time = new Date(message.created_at).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

// Decorate-sort-undecorate: messages without a usable created_at sort to the
// end, in their original relative order, so callers never need to special-case
// optimistic/malformed messages before merging them with dated ones.
export const sortByCreatedAtStable = <T extends TimestampedMessage>(messages: T[]): T[] =>
  messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => toTimestamp(a.message) - toTimestamp(b.message) || a.index - b.index)
    .map((entry) => entry.message);

// Appends a single message to an already-sorted list. Skips the full
// O(n log n) re-sort when the new message is already chronologically last --
// the common case for live socket/optimistic messages -- and only falls back
// to sortByCreatedAtStable when it actually needs to be inserted earlier.
export const appendSorted = <T extends TimestampedMessage>(messages: T[], message: T): T[] => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage || toTimestamp(message) >= toTimestamp(lastMessage)) {
    return [...messages, message];
  }
  return sortByCreatedAtStable([...messages, message]);
};
